import { useSyncExternalStore } from 'react';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { RenderAdapter } from '../types';
import {
    frameGeometrySourceOf,
    planeViewOf,
} from './ports/PlaneViewRenderAdapter';

/**
 * The engine view behind a tile's renderer, followed across mounts: the
 * renderer announces a mount on its cursor source, and the view read then
 * is the snapshot, so the caller re-renders when the view is replaced and
 * not on every cursor move.
 */
export function usePlaneViewOf(renderer: RenderAdapter): PlaneView | undefined {
    const source = renderer.getCursorSource();
    return useSyncExternalStore(source.subscribe, () => planeViewOf(renderer));
}

/**
 * The geometry of the frame on the canvas of the engine view behind a
 * tile's renderer. The geometry itself is the snapshot, so a frame
 * published between the render and the subscription is still picked up;
 * undefined before the first frame and for a non-engine renderer.
 */
export function useCanvasFrameGeometryOf(
    renderer: RenderAdapter
): CanvasFrameGeometry | undefined {
    const source = frameGeometrySourceOf(renderer);
    return useSyncExternalStore(
        source.subscribe,
        () => planeViewOf(renderer)?.canvasFrameGeometry
    );
}
